/**
 * ContextItemChip component
 * Displays a context item as a removable chip/badge
 */
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SessionContextItem } from '@/types/chatbot'
import { ContextItemTypeIcon, getTypeLabel } from './ContextItemTypeIcon'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface ContextItemChipProps {
  /** The context item to display */
  item: SessionContextItem
  /** Callback when remove button is clicked */
  onRemove?: (item: SessionContextItem) => void
  /** Whether the chip is in a disabled state */
  disabled?: boolean
  /** Display in compact mode */
  compact?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Format the display value based on type.
 * Truncates long values and formats appropriately.
 */
function formatDisplayValue(type: string, value: string): string {
  // For work items and PRs, just show the ID
  if (type.startsWith('ado:')) {
    return `#${value}`
  }
  
  // For wiki pages and files, show the path (potentially truncated)
  if (type === 'wiki:page' || type === 'file') {
    // Show last part of path if too long
    if (value.length > 30) {
      const parts = value.split('/')
      if (parts.length > 1) {
        return `.../${parts.slice(-2).join('/')}`
      }
      return `...${value.slice(-25)}`
    }
    return value
  }
  
  // For URLs, show truncated URL
  if (type === 'url') {
    try {
      const url = new URL(value)
      const display = url.hostname + url.pathname
      return display.length > 30 ? display.slice(0, 27) + '...' : display
    } catch {
      return value.length > 30 ? value.slice(0, 27) + '...' : value
    }
  }
  
  // For notes and others, truncate if needed
  return value.length > 30 ? value.slice(0, 27) + '...' : value
}

export function ContextItemChip({
  item,
  onRemove,
  disabled = false,
  compact = false,
  className,
}: ContextItemChipProps) {
  const typeLabel = getTypeLabel(item.type)
  const displayValue = formatDisplayValue(item.type, item.value)
  const fullTooltip = `${typeLabel}: ${item.value}`

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border bg-muted/50 text-foreground',
              compact ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm',
              disabled && 'opacity-50',
              className
            )}
            data-testid="context-item-chip"
            data-type={item.type}
          >
            <ContextItemTypeIcon
              type={item.type}
              size={compact ? 'sm' : 'sm'}
            />
            <span className="truncate max-w-[150px]">{displayValue}</span>
            {onRemove && !disabled && (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'rounded-full hover:bg-destructive/20 hover:text-destructive',
                  compact ? 'h-4 w-4' : 'h-5 w-5'
                )}
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove(item)
                }}
                aria-label={`Remove ${typeLabel} ${item.value}`}
              >
                <X className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
              </Button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{fullTooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
